import React, { useEffect, useState } from "react";
import { X, Calendar, Loader, Plus, Briefcase, MapPin, User as UserIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * AddInterviewModal — lightweight modal for scheduling an upcoming interview.
 *
 * Optionally accepts `analysisId` to tie this interview to an existing
 * tailored resume. When provided, we pre-fill role_title/role_company from
 * the analysis and lock the linkage.
 */
export default function AddInterviewModal({
  open,
  onClose,
  onCreated,
  analysisId = null,
  defaultRoleTitle = "",
  defaultRoleCompany = "",
}) {
  const [roleTitle, setRoleTitle] = useState("");
  const [roleCompany, setRoleCompany] = useState("");
  const [interviewerName, setInterviewerName] = useState("");
  const [interviewerLinkedin, setInterviewerLinkedin] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setRoleTitle(defaultRoleTitle || "");
      setRoleCompany(defaultRoleCompany || "");
      // Default to 3 days from now
      const d = new Date();
      d.setDate(d.getDate() + 3);
      setDate(d.toISOString().slice(0, 10));
      setTime("10:00");
      setError("");
    }
  }, [open, defaultRoleTitle, defaultRoleCompany]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!roleTitle || !roleCompany || !date || !time) {
      setError("Role title, company, date and time are required.");
      return;
    }
    setSubmitting(true);
    try {
      // Combine date + time into ISO datetime in user's local timezone, then to UTC ISO
      const localDt = new Date(`${date}T${time}:00`);
      const isoUtc = localDt.toISOString();
      const payload = {
        interview_at: isoUtc,
        role_title: roleTitle,
        role_company: roleCompany,
        interviewer_name: interviewerName || null,
        interviewer_linkedin_url: interviewerLinkedin || null,
        location: location || null,
        notes: notes || null,
        analysis_id: analysisId,
      };
      const { data } = await api.post("/interviews", payload);
      onCreated?.(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save interview.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(15, 27, 45, 0.55)" }}
      onClick={onClose}
      data-testid="add-interview-modal"
    >
      <div
        className="rm-card-static p-6 md:p-8 w-full max-w-xl my-8"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#FFFBEB", borderColor: "#0F1B2D" }}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest rm-text-orange mb-1"
                 style={{ fontWeight: 700 }}>
              · Interview Mode · Schedule
            </div>
            <h2 className="font-display uppercase text-2xl leading-tight"
                style={{ color: "#0F1B2D", letterSpacing: "-0.02em" }}>
              Lock in <span className="rm-text-orange">your interview.</span>
            </h2>
            <p className="text-[13px] mt-1" style={{ color: "#475569" }}>
              Resmo will remind you 7 days, 24 hours, and 1 hour before — with
              your bundled briefing ready to read or listen to.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rm-btn rm-btn-ghost text-xs px-2 py-2"
            data-testid="add-interview-close-btn"
          >
            <X size={14} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="add-interview-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="rm-label text-[11px]">
                <Calendar size={11} style={{ display: "inline", marginRight: 4 }} />
                Date
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="rm-input w-full"
                required
                data-testid="add-interview-date"
              />
            </div>
            <div>
              <label className="rm-label text-[11px]">Time</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="rm-input w-full"
                required
                data-testid="add-interview-time"
              />
            </div>
          </div>

          <div>
            <label className="rm-label text-[11px]">
              <Briefcase size={11} style={{ display: "inline", marginRight: 4 }} />
              Role title
            </label>
            <input
              value={roleTitle}
              onChange={(e) => setRoleTitle(e.target.value)}
              placeholder="Senior Product Manager"
              className="rm-input w-full"
              required
              data-testid="add-interview-role-title"
            />
          </div>

          <div>
            <label className="rm-label text-[11px]">Company</label>
            <input
              value={roleCompany}
              onChange={(e) => setRoleCompany(e.target.value)}
              placeholder="Acme Corp"
              className="rm-input w-full"
              required
              data-testid="add-interview-role-company"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="rm-label text-[11px]">
                <UserIcon size={11} style={{ display: "inline", marginRight: 4 }} />
                Interviewer name (optional)
              </label>
              <input
                value={interviewerName}
                onChange={(e) => setInterviewerName(e.target.value)}
                placeholder="Jane Doe"
                className="rm-input w-full"
                data-testid="add-interview-interviewer-name"
              />
            </div>
            <div>
              <label className="rm-label text-[11px]">LinkedIn URL (optional)</label>
              <input
                type="url"
                value={interviewerLinkedin}
                onChange={(e) => setInterviewerLinkedin(e.target.value)}
                placeholder="https://www.linkedin.com/in/jane-doe"
                className="rm-input w-full"
                data-testid="add-interview-interviewer-linkedin"
              />
            </div>
          </div>

          <div>
            <label className="rm-label text-[11px]">
              <MapPin size={11} style={{ display: "inline", marginRight: 4 }} />
              Location / Meeting link (optional)
            </label>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="https://meet.google.com/abc · or a physical address"
              className="rm-input w-full"
              data-testid="add-interview-location"
            />
          </div>

          <div>
            <label className="rm-label text-[11px]">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Bring portfolio examples · Confirm parking · etc."
              className="rm-input w-full"
              data-testid="add-interview-notes"
            />
          </div>

          {error && (
            <div className="rm-card-static p-2 text-xs font-mono-rm"
                 style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}
                 data-testid="add-interview-error">
              · {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 mt-4 flex-wrap">
            <button
              type="button"
              onClick={onClose}
              className="rm-btn rm-btn-ghost text-xs px-3 py-2"
              data-testid="add-interview-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rm-btn rm-btn-primary text-sm px-4 py-2.5"
              data-testid="add-interview-submit-btn"
            >
              {submitting ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
              {submitting ? "Saving…" : "Schedule"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
